import { Css, Step, useComputed } from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { stepperBarHeight, StepperProvider } from "src/components/stepper";
import { useRouteParams } from "src/hooks/useRouteParams";
import { DefineChangeStep } from "src/routes/projects/change-events/guided/components/1.DefineChangeStep";
import { GuidedChangeEventStepActions } from "src/routes/projects/change-events/guided/components/GuidedChangeEventStepActions";
import { WorkflowRunner } from "src/routes/projects/change-events/guided/components/WorkflowRunner";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { guidedWorkflows } from "src/routes/projects/change-events/guided/workflows/registry";
import { BaseFormInput, BaseFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { ProjectHeader } from "src/routes/projects/ProjectHeader";
import { ProjectParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";

export function CreateGuidedChangeEventPage() {
  // The base form (workflow + reason) is owned by the page and always exists; each workflow
  // owns its own form inside `WorkflowRunner`. They are merged at submit time.
  const baseFormState = useFormState({ config: baseConfig });

  return (
    <StepperProvider steps={initialSteps}>
      <GuidedChangeEventWizard baseFormState={baseFormState} />
    </StepperProvider>
  );
}

// Export for testing
export type { BaseFormInput };

const baseConfig: ObjectConfig<BaseFormInput> = {
  workflow: { type: "value", rules: [required] },
  reasonId: { type: "value", rules: [required] },
};

// Seeded into the stepper before a change type is picked. Once a workflow mounts, `WorkflowRunner`
// replaces these with the workflow's own steps. A stable module-level ref so the provider's
// initSteps sync effect runs once and doesn't fight the runner's `setSteps`.
const initialSteps: Step[] = [
  { label: "Describe Change", value: GuidedChangeEventStep.DefineChange, state: "incomplete" },
  { label: "Select New Config", value: GuidedChangeEventStep.SelectConfiguration, state: "incomplete", disabled: true },
  { label: "Review New Config", value: GuidedChangeEventStep.ReviewConfiguration, state: "incomplete", disabled: true },
  { label: "Review Impact", value: GuidedChangeEventStep.ReviewImpact, state: "incomplete", disabled: true },
  { label: "Review", value: GuidedChangeEventStep.Review, state: "incomplete", disabled: true },
];

// A stub for the pre-workflow actions bar; there is nothing to submit or advance to until a
// change type is chosen (which immediately swaps in the workflow's own actions).
const noWorkflowActions = { onSubmit: async () => {} };

type GuidedChangeEventWizardProps = { baseFormState: BaseFormState };

function GuidedChangeEventWizard(props: GuidedChangeEventWizardProps) {
  const { baseFormState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const workflowCode = useComputed(() => baseFormState.workflow.value, [baseFormState]);
  const workflow = workflowCode ? guidedWorkflows[workflowCode] : undefined;

  return (
    <div css={Css.mbPx(stepperBarHeight).$}>
      <ProjectHeader projectId={projectId} title="Create Change Event" createUrl={createChangeEventsUrl} />
      <div css={Css.df.jcc.$}>
        <div css={Css.wPx(760).$}>
          {workflow ? (
            // Keyed by workflow so switching resets the workflow form and keeps `useActions` stable.
            <WorkflowRunner key={workflowCode} workflow={workflow} base={baseFormState} projectId={projectId} />
          ) : (
            <>
              <DefineChangeStep formState={baseFormState} />
              <GuidedChangeEventStepActions
                projectId={projectId}
                actions={noWorkflowActions}
                canAdvance={false}
                canSubmit={false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
